import React, { useState, useEffect } from 'react';
import {
    Card, Table, Input, Space, Typography, Tag, Row, Col, DatePicker, Select, Statistic
} from 'antd';
import {
    SearchOutlined, DollarOutlined, ImportOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PurchasesList = () => {
    const { t } = useTranslation(['pharmacy', 'common']);
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

    useEffect(() => {
        fetchPurchases();
    }, [dateRange]);

    const fetchPurchases = async (page = 1) => {
        try {
            setLoading(true);
            let url = `/pharmacy/purchases?page=${page}&limit=${pagination.pageSize}`;

            if (dateRange && dateRange[0] && dateRange[1]) {
                url += `&startDate=${dateRange[0].format('YYYY-MM-DD')}&endDate=${dateRange[1].format('YYYY-MM-DD')}`;
            }

            const res = await api.get(url);
            const data = res.data;

            setPurchases(data.data || []);
            setPagination(prev => ({
                ...prev,
                current: page,
                total: data.total || data.count || 0
            }));
        } catch (error) {
            console.error('Error fetching purchases:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredPurchases = search
        ? purchases.filter(p =>
            (p.purchaseNumber || '').toLowerCase().includes(search.toLowerCase()) ||
            (p.supplier || '').toLowerCase().includes(search.toLowerCase()) ||
            (p.invoiceNumber || '').toLowerCase().includes(search.toLowerCase())
        )
        : purchases;

    const totalSpent = filteredPurchases.reduce((sum, p) => sum + (p.totalAmount || 0), 0);
    const totalItems = filteredPurchases.reduce((sum, p) => sum + (p.items?.length || 0), 0);

    const columns = [
        {
            title: t('pharmacy:purchasesList.purchaseNumber'),
            dataIndex: 'purchaseNumber',
            key: 'purchaseNumber',
            render: (text) => <Text strong style={{ color: '#1890ff' }}>{text}</Text>,
            width: 150
        },
        {
            title: t('pharmacy:common.date'),
            dataIndex: 'purchaseDate',
            key: 'purchaseDate',
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
            width: 120,
            sorter: (a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate)
        },
        {
            title: t('pharmacy:common.supplier'),
            dataIndex: 'supplier',
            key: 'supplier',
            width: 160
        },
        {
            title: t('pharmacy:common.invoiceNum'),
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber',
            width: 120,
            render: (v) => v || '-'
        },
        {
            title: t('pharmacy:common.items'),
            dataIndex: 'items',
            key: 'items',
            render: (items) => items?.length || 0,
            width: 70,
            align: 'center'
        },
        {
            title: t('pharmacy:common.currency'),
            dataIndex: 'currency',
            key: 'currency',
            width: 80,
            render: (c) => <Tag>{c}</Tag>
        },
        {
            title: t('pharmacy:common.total'),
            dataIndex: 'totalAmount',
            key: 'total',
            width: 140,
            render: (amount, record) => (
                <Text strong>
                    {record.currency === 'IQD' ? `IQD ${amount?.toLocaleString()}` : `$${amount?.toFixed(2)}`}
                </Text>
            ),
            sorter: (a, b) => a.totalAmount - b.totalAmount
        },
        {
            title: t('pharmacy:common.payment'),
            dataIndex: 'paymentMethod',
            key: 'payment',
            width: 110
        },
        {
            title: t('pharmacy:common.status'),
            dataIndex: 'paymentStatus',
            key: 'status',
            width: 90,
            render: (status) => (
                <Tag color={status === 'Paid' ? 'green' : 'orange'}>{t(`pharmacy:common.paymentStatuses.${status}`, status)}</Tag>
            )
        },
        {
            title: t('pharmacy:purchasesList.createdBy'),
            dataIndex: 'createdBy',
            key: 'createdBy',
            render: (u) => u?.name || '-',
            width: 110
        }
    ];

    const expandedRowRender = (record) => (
        <Table
            dataSource={record.items}
            rowKey={(item, idx) => `${item.pharmacyItemId?._id || item.pharmacyItemId}-${idx}`}
            pagination={false}
            size="small"
            columns={[
                { title: t('pharmacy:common.item'), dataIndex: 'itemName', key: 'itemName' },
                { title: t('pharmacy:common.qty'), dataIndex: 'quantity', key: 'quantity', width: 60 },
                {
                    title: t('pharmacy:common.costPrice'), dataIndex: 'costPrice', key: 'costPrice', width: 120,
                    render: (v) => record.currency === 'IQD' ? `IQD ${v?.toLocaleString()}` : `$${v?.toFixed(2)}`
                },
                {
                    title: t('pharmacy:common.total'), dataIndex: 'total', key: 'total', width: 120,
                    render: (v) => <Text strong>{record.currency === 'IQD' ? `IQD ${v?.toLocaleString()}` : `$${v?.toFixed(2)}`}</Text>
                }
            ]}
        />
    );

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}><ImportOutlined /> {t('pharmacy:purchasesList.title')}</Title>

            {/* Stats */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={12} sm={8} lg={6}>
                    <Card size="small">
                        <Statistic
                            title={t('pharmacy:purchasesList.totalPurchases')}
                            value={filteredPurchases.length}
                            prefix={<ImportOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} lg={6}>
                    <Card size="small">
                        <Statistic
                            title={t('pharmacy:purchasesList.totalSpent')}
                            value={totalSpent}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                            precision={2}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} lg={6}>
                    <Card size="small">
                        <Statistic
                            title={t('pharmacy:purchasesList.totalItemsPurchased')}
                            value={totalItems}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} sm={8} lg={6}>
                        <Input
                            placeholder={t('pharmacy:purchasesList.searchPlaceholder')}
                            prefix={<SearchOutlined />}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={8} lg={6}>
                        <RangePicker
                            style={{ width: '100%' }}
                            onChange={(dates) => setDateRange(dates)}
                            value={dateRange}
                        />
                    </Col>
                </Row>
            </Card>

            {/* Purchases Table */}
            <Card>
                <Table
                    dataSource={filteredPurchases}
                    columns={columns}
                    rowKey="_id"
                    loading={loading}
                    size="small"
                    expandable={{ expandedRowRender }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: false,
                        showTotal: (total) => t('pharmacy:purchasesList.totalCount', { total }),
                        onChange: (page) => fetchPurchases(page)
                    }}
                    scroll={{ x: 1100 }}
                />
            </Card>
        </div>
    );
};

export default PurchasesList;
